import { PageHeader } from "../../../components/ui/page";
import { PageNavigation } from "../../../components/layout/PageNavigation";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { LeaveActionToast } from "../../leave-management/components/LeaveActionToast";
import { getEmpObj } from "../../../utils/sessionDB";
import { cacheTicketRemarks, getTicket, pickTicketRemarks, readCachedTicketRemarks, updateTicket } from "../../../services/ticketTrackerService";
import { TicketAuditHistory, TicketPriorityBadge, TicketStatusBadge } from "../components";
import {
  EMPTY_TICKET_FORM,
  TicketFormFields,
  TicketProgressField,
  mapTicketToForm,
  validateTicketForm,
} from "../components/TicketFormFields";

export function TicketDetailPage() {
  const { requestId } = useParams();
  const location = useLocation();

  const [form, setForm] = useState(EMPTY_TICKET_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [toast, setToast] = useState(null);
  const [banner, setBanner] = useState(null);
  const [auditRefreshKey, setAuditRefreshKey] = useState(0);
  const [displayTitle, setDisplayTitle] = useState("");
  const [displayMeta, setDisplayMeta] = useState({ status: "", priority: "", requestType: "" });

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);


  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    setBanner(null);

    getTicket(requestId)
      .then((ticket) => {
        if (cancelled) return;
        const fromNav = location.state?.ticket;
        const mapped = mapTicketToForm(
          {
            ...(fromNav && typeof fromNav === "object" ? fromNav : {}),
            ...(ticket && typeof ticket === "object" ? ticket : {}),
            remarks:
              pickTicketRemarks(ticket) ||
              pickTicketRemarks(fromNav) ||
              readCachedTicketRemarks(requestId) ||
              "",
          },
          getEmpObj().first_name
        );
        setForm(mapped);
      setReadOnly(["resolved", "closed", "rejected"].includes(mapped.status.toLowerCase()));
        setDisplayTitle(mapped.title || "Untitled ticket");
        setDisplayMeta({
          status: mapped.status,
          priority: mapped.priority,
          requestType: mapped.requestType,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setForm(EMPTY_TICKET_FORM);
        setLoadError(err?.message || "Failed to load ticket.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [requestId, location.state]);

  function updateField(key, value) {
    if (readOnly || saving) return;
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "service") {
        next.module = "";
        next.subModule = "";
      } else if (key === "module") {
        next.subModule = "";
      }
      return next;
    });
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  async function handleSave(e) {
    e.preventDefault();
    if (saving || readOnly) return;

    const errors = validateTicketForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    setBanner(null);
    try {
      const updated = await updateTicket(requestId, form);
      const merged =
        updated && typeof updated === "object"
          ? {
              ...form,
              ...updated,
              remarks: pickTicketRemarks(updated) || form.remarks,
              description: updated.description ?? updated.desc ?? form.description,
              assignee: updated.assignee ?? updated.assigneeName ?? form.assignee,
              createdBy: updated.createdBy ?? updated.createdByName ?? form.createdBy,
            }
          : form;
      const mapped = mapTicketToForm(merged, form.createdBy);
      cacheTicketRemarks(requestId, mapped.remarks);
      setForm(mapped);
      setReadOnly(["resolved", "closed", "rejected"].includes(mapped.status.toLowerCase()));
      setDisplayTitle(mapped.title || "Untitled ticket");
      setDisplayMeta({
        status: mapped.status,
        priority: mapped.priority,
        requestType: mapped.requestType,
      });
      setAuditRefreshKey((k) => k + 1);
      setToast({
        message: `Ticket "${mapped.title.trim()}" updated successfully.`,
        type: "success",
      });
    } catch (err) {
      setBanner("Couldn't update this ticket: " + (err.message || "Unknown error"));
      setToast({
        message: err?.message || "Couldn't update ticket.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AppLayout formLayout="standard" hideBreadcrumb>
        <PageNavigation>
          <PageHeader className="mb-0 w-full" back={<PageBackLink to="/psm/tickets" />} title="Ticket Detail" />
        </PageNavigation>
        <div className="py-10 text-center text-sm per-file-hint">
          Loading ticket…
        </div>
      </AppLayout>
    );
  }

  if (loadError) {
    return (
      <AppLayout formLayout="standard" hideBreadcrumb>
        <PageNavigation>
          <PageHeader className="mb-0 w-full" back={<PageBackLink to="/psm/tickets" />} title="Ticket Detail" />
        </PageNavigation>
        <div className="py-10 text-center text-sm text-error">{loadError}</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout formLayout="standard" hideBreadcrumb overlay={<LeaveActionToast toast={toast} />}>
      <PageNavigation>
        <PageHeader
          className="mb-0 w-full"
          back={<PageBackLink to="/psm/tickets" />}
          title={displayTitle || "Ticket Detail"}
          action={
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <TicketStatusBadge status={displayMeta.status || form.status} />
              <TicketPriorityBadge priority={displayMeta.priority || form.priority} />
              {displayMeta.requestType || form.requestType ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-workspace per-file-hint">
                  {displayMeta.requestType || form.requestType}
                </span>
              ) : null}
            </div>
          }
        />
      </PageNavigation>

      <form onSubmit={handleSave}>
        {banner ? (
          <div className="flex items-center gap-2 rounded-lg border border-error/30 bg-error/5 px-3 py-2 text-xs text-error mb-2">
            {banner}
          </div>
        ) : null}

        <div className="bg-white rounded-xl shadow-[0_8px_24px_-16px_rgba(30,51,131,0.3)] border border-white/60 overflow-hidden mb-4 form-surface form-section">
          <div className="flex items-center justify-between gap-3 mb-2.5">
            <h3 className="text-sm font-semibold text-foreground">Ticket Details</h3>
          </div>
          <TicketFormFields
            form={form}
            updateField={updateField}
            fieldErrors={fieldErrors}
            mode="edit"
            readOnly={readOnly || saving}
            showRequestId
            requestId={requestId}
          />
          <div className="mt-4 flex flex-wrap items-center justify-end gap-4 border-t border-(--color-text-border) pt-3">
            <TicketProgressField form={form} updateField={updateField} readOnly={readOnly || saving} />
            {readOnly ? (
              <Button type="button"
                className="h-8 px-3 rounded-lg text-xs font-semibold bg-primary text-white hover:bg-accent"
                onClick={() => {
                  setReadOnly(false);
                  setForm((prev) => ({ ...prev, status: "Open" }));
                }}>
                Reopen
              </Button>
            ) : (
            <Button
              type="submit"
              className="h-8 px-3 rounded-lg text-xs font-semibold bg-primary text-white hover:bg-accent"
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            )}
          </div>
        </div>
      </form>

      <TicketAuditHistory requestId={requestId} refreshKey={auditRefreshKey} />
    </AppLayout>
  );
}
